import { type ReactNode, useState } from "react";

import {
  Badge,
  Box,
  Button,
  Checkbox,
  Column,
  Row,
  TextWithTooltip,
  Tooltip,
} from "@hightouch/htui";

import { DebouncedSearchInput } from "src/components/debounced-search-input";
import type { OrderBy } from "src/graphql";
import { ColumnTypeCell } from "src/pages/decision-engines/components/column-type-cell";
import { Table, type TableColumn } from "src/ui/table";
import { lodashOrderBy } from "src/ui/table/use-table-config";

export type SourceColumn = {
  uniqueKey: string;
  name: string;
  alias?: string | null;
  type: string;
  // Selected, but no longer on the source model.
  isStale: boolean;
};

type SortKey = "name" | "type";

type Props<T extends SourceColumn> = {
  // Rendered in this order unless the user sorts.
  columns: T[];
  isSelected: (column: T) => boolean;
  onToggle: (column: T) => void;
  onToggleAll: (visibleColumns: T[], select: boolean) => void;
  isColumnDisabled?: (column: T) => boolean;
  isSelectAllDisabled?: boolean;
  renderBadges?: (column: T) => ReactNode;
  extraColumns?: TableColumn<T>[];
  isDimmed?: boolean;
  // Pads the toolbar to line up with a full-bleed table in a page pane.
  isInset?: boolean;
};

export function SourceColumnList<T extends SourceColumn>({
  columns,
  isSelected,
  onToggle,
  onToggleAll,
  isColumnDisabled,
  isSelectAllDisabled = false,
  renderBadges,
  extraColumns = [],
  isDimmed = false,
  isInset = false,
}: Props<T>) {
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<OrderBy>("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [hideUnselected, setHideUnselected] = useState(false);

  const query = searchQuery.toLowerCase();
  const filtered = columns.filter(
    (column) =>
      (!query || (column.alias || column.name).toLowerCase().includes(query)) &&
      (!hideUnselected || isSelected(column)),
  );
  const visibleColumns = sortKey
    ? lodashOrderBy(filtered, sortKey, sortDirection, {
        name: (column) => column.alias || column.name,
      })
    : filtered;

  // With no extra columns, Type is last, so it takes the spare width instead of Source column.
  const isTypeLast = extraColumns.length === 0;

  const toggleableColumns = visibleColumns.filter(
    (column) =>
      !column.isStale && (isSelected(column) || !isColumnDisabled?.(column)),
  );
  const someVisibleSelected = toggleableColumns.some(isSelected);
  const allVisibleSelected =
    toggleableColumns.length > 0 && toggleableColumns.every(isSelected);

  const onSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection("asc");
    } else if (sortDirection === "asc") {
      setSortDirection("desc");
    } else {
      setSortKey(null);
      setSortDirection("asc");
    }
  };

  const columnSortState = (key: SortKey) => ({
    sortDirection: (sortKey === key ? sortDirection : null) as OrderBy | null,
    onClick: () => onSort(key),
  });

  return (
    <Column h="100%" minHeight={0}>
      <Row
        px={isInset ? 6 : 0}
        pt={isInset ? 3 : 0}
        pb={3}
        gap={4}
        align="center"
        bg="white"
      >
        <DebouncedSearchInput
          initialValue=""
          onDebouncedChange={setSearchQuery}
          width="100%"
          placeholder="Search columns…"
        />
        <Box>
          <Button
            variant="tertiary"
            onClick={() => setHideUnselected((value) => !value)}
            size="sm"
            color={hideUnselected ? "primary.base" : "text.secondary"}
          >
            {hideUnselected ? "Show all" : "Hide unselected"}
          </Button>
        </Box>
      </Row>

      <Box opacity={isDimmed ? 0.5 : 1} overflow="auto" h="100%" minWidth={0}>
        <Table
          top={0}
          data={visibleColumns}
          primaryKey="uniqueKey"
          columns={[
            {
              name: "",
              min: "max-content",
              max: "max-content",
              header: () => (
                <Checkbox
                  isChecked={allVisibleSelected}
                  isDisabled={isSelectAllDisabled}
                  isIndeterminate={someVisibleSelected && !allVisibleSelected}
                  onChange={() =>
                    onToggleAll(toggleableColumns, !allVisibleSelected)
                  }
                  aria-label="Select all columns"
                />
              ),
              cell: (column) => (
                <Checkbox
                  isChecked={isSelected(column)}
                  isDisabled={isColumnDisabled?.(column) ?? false}
                  onChange={() => onToggle(column)}
                  aria-label={`Select ${column.name}`}
                />
              ),
            },
            {
              name: "Source column",
              min: "140px",
              max: isTypeLast ? "max-content" : undefined,
              ...columnSortState("name"),
              cell: (column) => (
                <Row gap={2} align="center" minWidth={0} overflow="hidden">
                  <TextWithTooltip
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      ...(column.isStale
                        ? { color: "text.tertiary", fontStyle: "italic" }
                        : {}),
                    }}
                  >
                    {column.alias || column.name}
                  </TextWithTooltip>
                  {column.isStale && (
                    <Tooltip message="This column no longer exists in the source model">
                      <Badge size="sm" variant="error">
                        Removed
                      </Badge>
                    </Tooltip>
                  )}
                  {renderBadges?.(column)}
                </Row>
              ),
            },
            {
              name: "Type",
              min: "min-content",
              max: isTypeLast ? "1fr" : "min-content",
              ...columnSortState("type"),
              cell: (column) => (
                <ColumnTypeCell type={column.type} isStale={column.isStale} />
              ),
            },
            ...extraColumns,
          ]}
          placeholder={{
            title: "No columns available",
          }}
        />
      </Box>
    </Column>
  );
}
